import { isBasicType, isObject } from "./utils/utils";
/**
 * Date to 18 September 2025 format
 */
function dateToStr(date) {
    return `${date.getDate()} ${date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
    })}`;
}
export function formatInferredValue(value, fieldConfig) {
    if (isBasicType(value)) {
        return value;
    }
    if (value == null || value == undefined) {
        return "";
    }
    switch (fieldConfig.type) {
        case "date":
            return formatDate(value);
        case "fullName":
            return formatFullName(value);
        case "address":
            return formatAddress(value);
        case "phone":
            return formatPhone(value);
        // case "money":
        //   return formatMoney(value as MoneyValue);
        case "percentage":
            return formatPercentage(value);
        case "timeOfDay":
            return formatTimeOfDay(value);
        case "duration":
            return formatDuration(value);
        case "url":
            return formatUrl(value);
        case "file":
            return value.fileName;
        case "scheduling":
            return formatScheduling(value);
        default:
            // For other complex types, join values with spaces
            return Object.values(value).join(' ');
    }
}
function formatUrl(value) {
    const { protocol, domain, path, query } = value;
    return `${protocol}://${domain}/${path}${query ? `?${query}` : ''}`;
}
function formatScheduling(value) {
    if (typeof value === "string") {
        return value;
    }
    if (!value.slotData) {
        return "";
    }
    let v = value;
    let start;
    let end;
    if (typeof v.slotData.start === "string") {
        start = new Date(v.slotData.start).toISOString();
    }
    if (typeof v.slotData.end === "string") {
        end = new Date(v.slotData.end).toISOString();
    }
    return `SlotData: ${start} - ${end}, EventId: ${v.eventId}, Participants: ${JSON.stringify(v.participants)}`;
}
function formatDate(value) {
    const { day, month, year } = value;
    // Handle partial dates gracefully
    if (!day && !month && !year)
        return '';
    // If we have a complete date, create a Date object and format it nicely
    if (day && month && year) {
        try {
            const date = new Date(year, month - 1, day); // month is 0-indexed in Date constructor
            // Check if the date is valid
            if (isNaN(date.getTime())) {
                // Fallback to manual formatting if date is invalid
                return `${month}/${day}/${year}`;
            }
            // Use natural, human-friendly date formatting
            return date.toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
            });
        }
        catch {
            // Fallback if date construction fails
            return `${month}/${day}/${year}`;
        }
    }
    // Handle partial dates
    if (month && year) {
        try {
            const date = new Date(year, month - 1, 1);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long'
                });
            }
        }
        catch {
            // Fallback
        }
        const months = [
            'January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'
        ];
        const monthName = months[month - 1] || month.toString();
        return `${monthName} ${year}`;
    }
    if (year)
        return year.toString();
    if (month && day) {
        try {
            // Use current year for partial date
            const currentYear = new Date().getFullYear();
            const date = new Date(currentYear, month - 1, day);
            if (!isNaN(date.getTime())) {
                return date.toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric'
                });
            }
        }
        catch {
            // Fallback
        }
        return `${month}/${day}`;
    }
    return [day, month, year].filter(v => v).join('/');
}
function formatFullName(value) {
    const { firstName, middleName, lastName } = value;
    return [firstName, middleName, lastName].filter(v => v).join(' ');
}
function formatAddress(value) {
    const { addressLine1, city, state, zip, country } = value;
    const parts = [];
    if (addressLine1)
        parts.push(addressLine1);
    // Format city, state zip on one line
    const cityStateParts = [];
    if (city)
        cityStateParts.push(city);
    if (state)
        cityStateParts.push(state);
    if (zip)
        cityStateParts.push(zip);
    if (cityStateParts.length > 0) {
        parts.push(cityStateParts.join(', '));
    }
    if (country)
        parts.push(country);
    return parts.join(', ');
}
function formatPhone(value) {
    const { countryCode, areaCode, number } = value;
    // Build phone number from parts
    const parts = [];
    if (countryCode)
        countryCode.startsWith("+") ? parts.push(countryCode) : parts.push(`+${countryCode}`);
    if (areaCode)
        parts.push(areaCode);
    if (number)
        parts.push(number);
    return parts.join(' ');
}
// function formatMoney(value: MoneyValue): string {
//   const { currency, amount } = value;
//   if (amount != null) {
//     if (currency) {
//       // Use simple, natural format like "$500" or "€250"
//       const currencySymbols: { [key: string]: string } = {
//         'USD': '$',
//         'EUR': '€',
//         'GBP': '£',
//         'JPY': '¥',
//         'CAD': 'C$',
//         'AUD': 'A$'
//       };
//       const symbol = currencySymbols[currency.toUpperCase()] || currency.toUpperCase() + ' ';
//       // Format without cents if it's a whole number
//       if (amount % 1 === 0) {
//         return `${symbol}${amount.toLocaleString()}`;
//       } else {
//         return `${symbol}${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
//       }
//     } else {
//       // Default to $ if no currency specified
//       if (amount % 1 === 0) {
//         return `$${amount.toLocaleString()}`;
//       } else {
//         return `$${amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
//       }
//     }
//   }
//   return '';
// }
function formatPercentage(value) {
    return `${value}%`;
}
function formatTimeOfDay(value) {
    const { hour, minute } = value;
    if (hour != null) {
        const mins = minute || 0;
        // Convert to 12-hour format with AM/PM
        let displayHour = hour;
        let period = 'AM';
        if (hour === 0) {
            displayHour = 12; // Midnight = 12 AM
        }
        else if (hour === 12) {
            period = 'PM'; // Noon = 12 PM
        }
        else if (hour > 12) {
            displayHour = hour - 12;
            period = 'PM';
        }
        // Format minutes
        if (mins === 0) {
            return `${displayHour} ${period}`;
        }
        else {
            const formattedMinute = mins.toString().padStart(2, '0');
            return `${displayHour}:${formattedMinute} ${period}`;
        }
    }
    return '';
}
function formatDuration(_value) {
    // Assuming duration is in minutes, format in natural language
    let value;
    if (isObject(_value)) {
        value = _value.duration;
    }
    else if (typeof _value === "number") {
        value = _value;
    }
    else {
        return _value;
    }
    if (value >= 60) {
        const hours = Math.floor(value / 60);
        const minutes = value % 60;
        if (minutes === 0) {
            return `${hours} h`;
        }
        else {
            return `${hours} h ${minutes} min`;
        }
    }
    else {
        return `${value} min`;
    }
}
